import { Flex, MantineStyleProps, Text } from '@mantine/core';
import { BaseFinancialItem } from 'src/financial/financial-data/hooks/useFinancialItems';
import { DataTable, DataTableColumn } from 'mantine-datatable';
import React, { useMemo } from 'react';
import useSortableData from 'src/common/components/table/hooks/useSortableData';

type FinancialItemGenericTable<T extends BaseFinancialItem> = {
  financialItems: T[];
  fieldsRenderingRules: {
    headerTitle: string;
    render: (item: T) => React.ReactNode;
    sortable?: boolean;
    sortKey?: string;
  }[];
  pl?: MantineStyleProps['pl'];
  onRowClick: (clickedItem: BaseFinancialItem) => void;
};

export const formatDate = (date: string | undefined) => {
  if (!date) {
    return '';
  }
  const wrappedDate = new Date(date);
  return `${wrappedDate.getDate().toString().padStart(2, '0')}/${(wrappedDate.getMonth() + 1)
    .toString()
    .padStart(2, '0')}/${wrappedDate.getFullYear()}`;
};
export function FinancialItemGenericTable<T extends BaseFinancialItem>({
  financialItems,
  fieldsRenderingRules,
  pl = 50,
  onRowClick,
}: FinancialItemGenericTable<T>) {
  const dataTableColumns: DataTableColumn<T>[] = useMemo(
    () =>
      fieldsRenderingRules.map((rule, index) => ({
        accessor: rule.sortKey || rule.headerTitle,
        title: rule.headerTitle,
        sortable: rule.sortable ?? false,
        sortKey: rule.sortKey || rule.headerTitle,
        textAlign: index === fieldsRenderingRules.length - 1 ? ('right' as const) : undefined,
        render: (record: T) => {
          const value = rule.render(record);
          const displayValue = value || (
            <Text c="gray.7" inherit>
              Unknown
            </Text>
          );
          const isFirstColumn = index === 0;

          if (isFirstColumn) {
            return (
              <Flex direction={'row'} gap={'xxs'} pl={pl}>
                <img src={record.logoUri} height={20} width={20} alt={record.description} />
                {displayValue}
              </Flex>
            );
          }

          return displayValue;
        },
      })),
    [fieldsRenderingRules, pl],
  );

  const { sortStatus, setSortStatus, sortedData } = useSortableData(
    financialItems,
    dataTableColumns,
  );

  return (
    <>
      <DataTable
        minHeight={150}
        columns={dataTableColumns}
        records={sortedData}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        variant="accordion-table"
        withTableBorder={false}
        withRowBorders={false}
        onRowClick={({ record }) => onRowClick(record)}
        noRecordsText="No items added"
      />
    </>
  );
}
